import { useAdminDetails } from "@/v2/stores";
import { useEffectiveWhitelabelId } from "./useEffectiveWhitelabelId";
import { useFeatureFlags } from "./useFeatureFlags";
import { useOrganizationId } from "./useOrganizationId";

export interface VehicleModelsAccess {
  /** True only when the catalogue is both enabled and usable by this admin. */
  enabled: boolean;
  /**
   * Null until the whitelabel resolves, or when there is no organization. Every
   * vehicle-model endpoint needs it, and a super admin must send it explicitly,
   * so it is returned here rather than re-derived at each call site.
   */
  organizationId: string | null;
}

/**
 * Whether the catalogue's sidebar entry and routes should exist at all.
 *
 * Deliberately weaker than `useVehicleModelsEnabled`, and the difference is the
 * unresolved case. The flag is per-organization, an organization is reached
 * through a whitelabel, and a super admin sitting on "All whitelabels" has not
 * picked one — so the flag cannot be read yet. Hiding the entry there made the
 * container's own "pick a whitelabel" state unreachable: the route was gated on
 * a flag that could only be false, so the message could never render. Showing
 * the entry and letting the page ask is the same shape App Configurations uses.
 *
 * Only super admins ever see the unresolved case. A whitelabel-scoped admin
 * carries their id on `adminDetails`, so their flag always resolves, and city
 * admins are refused before the flag is consulted either way. Nothing about an
 * organization is revealed by the entry existing — the page behind it shows a
 * prompt, not a catalogue.
 */
export function shouldOfferVehicleModels(
  featureFlags: { vehicleModels: boolean },
  adminDetails: { cityId: number | null } | null | undefined,
  whitelabelId: number | null
): boolean {
  // Unchanged, and checked first: a model spans the organization, so the
  // backend refuses city admins on every one of these endpoints.
  if (adminDetails?.cityId != null) return false;

  return featureFlags.vehicleModels || whitelabelId == null;
}

/**
 * Whether this admin can use the vehicle-model catalogue, and against which
 * organization.
 *
 * Two conditions, and the second is easy to forget: the organization has opted
 * in, *and* the caller is not a city-scoped admin. The backend refuses city
 * admins on every vehicle-model endpoint — a model spans the whole organization,
 * so editing one reaches into cities they do not administer. Gating on the flag
 * alone would render a section whose every request comes back 403.
 *
 * The route and sidebar gates cannot use this hook: DashboardScreen hands them
 * `featureFlags` and `adminDetails` as props and they apply the same pair of
 * conditions inline. Any other gate belongs here.
 */
export function useVehicleModelsEnabled(): VehicleModelsAccess {
  const whitelabelId = useEffectiveWhitelabelId();
  const organizationId = useOrganizationId(whitelabelId);
  const { vehicleModels } = useFeatureFlags(organizationId);
  const adminDetails = useAdminDetails();

  return {
    enabled: vehicleModels && adminDetails?.cityId == null,
    organizationId,
  };
}
